import { VehicleState } from '../enums/DocStates'
import BodyByType from '../interfaces/BodyByType'
import VehicleBody from '../interfaces/VehicleBody'
import { Expense, ExpenseFormat } from '../interfaces/VehicleBase'
import { NvvAction, ReservaAction } from '../enums/DocActions'
import Credit from '../interfaces/Credit'
import { Payment } from '../interfaces/Reservation'
import { PaymentEx } from '../interfaces/SalenoteDto'

import genderTranslate from './genderTranslate'

import { AppPath } from '@/enums/AppPath'

/*
Cambia el formato de género para el display 
*/
export const transformGender = (gender?: string): string => {
  if (!gender) {
    return 'No especificado'
  }

  return genderTranslate[gender] || 'No especificado'
}

export const fechaIsoToYYYY_MM_DD = (date?: string): string => {
  if (!date) {
    return ''
  }

  return date.split('T')[0]
}

export const fechaIsoToDD_MM_YYYY = (date?: string): string => {
  if (!date) {
    return ''
  }
  const [year, month, day] = date.split('T')[0].split('-')

  return `${day}-${month}-${year}`
}

export const fechaIsoToTime = (date?: string): string => {
  if (!date) {
    return ''
  }

  const mydate = new Date(date)
  const hh = String(mydate.getHours()).padStart(2, '0')
  const min = String(mydate.getMinutes()).padStart(2, '0')

  return `${hh}:${min}`
}

export const fechaIsoToFullWithT = (date?: string): string => {
  if (!date) {
    return ''
  }

  const parts = date.split('T')
  const mydate = parts[0]
  // EN parts 1 tenemos algo como 15:02:04.nnnZ
  const hhmm = parts[1].split(':')

  return `${mydate}T${hhmm[0]}:${hhmm[1]}`
}

export const fechaIsoToIso = (date?: string): string => {
  if (!date) {
    return ''
  }

  const mydate = new Date(date)
  const day = String(mydate.getDate()).padStart(2, '0')
  const month = String(mydate.getMonth() + 1).padStart(2, '0') // Months are zero-based
  const year = mydate.getFullYear()
  const hh = String(mydate.getHours()).padStart(2, '0')
  const min = String(mydate.getMinutes()).padStart(2, '0')

  return `${year}-${month}-${day}T${hh}:${min}`
}

export const fechaIsoToFull = (date?: string): string => {
  if (!date) {
    return ''
  }

  const mydate = new Date(date)

  return fechaDateToFull(mydate)
}

export const fechaDateToFull = (mydate: Date): string => {
  const day = String(mydate.getDate()).padStart(2, '0')
  const month = String(mydate.getMonth() + 1).padStart(2, '0') // Months are zero-based
  const year = mydate.getFullYear()
  const hh = String(mydate.getHours()).padStart(2, '0')
  const min = String(mydate.getMinutes()).padStart(2, '0')

  return `${day}-${month}-${year} ${hh}:${min}`
}

export const formatDateToDDMMYYYY = (date: Date | undefined): string => {
  if (typeof date === 'undefined') {
    date = new Date()
  }

  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0') // Months are zero-based
  const year = date.getFullYear()

  return `${day}-${month}-${year}`
}

export const formatIsoToDDMMYYYY = (strIso: string | undefined): string => {
  if (typeof strIso === 'undefined') {
    return ''
  }

  const date = new Date(strIso)

  // Check for invalid date (includes Unix epoch check for "0" string bug)
  if (isNaN(date.getTime()) || date.getTime() === 0) {
    console.warn('[formatIsoToDDMMYYYY] Invalid date received:', strIso)

    return 'Fecha inválida'
  }

  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0') // Months are zero-based
  const year = date.getFullYear()

  return `${day}-${month}-${year}`
}
/*
Fecha viene YYYY-MM-DD o DD-MM-YYYY. La damos vuelta 
*/
export const formatDate = (date: string | undefined): string => {
  if (typeof date === 'undefined') {
    return ''
  }

  const arr = date.split('-')
  const day = arr[2]
  const month = arr[1]
  const year = arr[0]

  return `${day}-${month}-${year}`
}
/*
Fecha viene YYYY-MM-DD o YYYY-MM-DDT==:03 etc (con infomacion de zona)
Si no viene la T, la damos vuelta. Si viene, tambian, pero antes debemos
convetirla
*/
export const formatDateEx = (date: string | undefined): string => {
  if (typeof date === 'undefined') {
    return ''
  }
  const ptr = date.indexOf('T')

  if (ptr < 0) {
    return formatDate(date)
  }

  // Es una fecha full: la convertimos
  return formatIsoToDDMMYYYY(date)
}

/*
Fecha viene YYYY-MM-DD. Retornamos solo el mes y el año
*/
export const formatPeriod = (date: string | undefined): string => {
  if (typeof date === 'undefined') {
    return ''
  }

  const arr = date.split('-')

  const month = arr[1]
  const year = arr[0]

  return `${month}-${year}`
}

export const formatCurrency = (value: number): string => {
  const formatted = new Intl.NumberFormat('CLP', {
    style: 'currency',
    currency: 'CLP',
  }).format(value)

  if (formatted === '$NaN') {
    return ''
  }

  return formatted
}

export const formatWithThousandSep = (value: number, locale: string = navigator.language): string => {
  if (!value) {
    return '0'
  }

  return new Intl.NumberFormat(locale).format(value)
}

export const getBodiesByType = (type: string, array: VehicleBody[]): BodyByType[] =>
  array
    .filter((obj) => obj.type === type)
    .map((obj) => ({
      description: obj.description,
      body: obj.body,
    }))

/*
Funcion devuelve true si esta expirado (es decir, dat1 < hoy)
*/
export const isExpired = (date1: Date) => {
  const hoy = new Date()
  const year1 = date1.getFullYear()
  const month1 = date1.getMonth() // Note: Months are 0-indexed (0 = January, 11 = December)
  const day1 = date1.getDate()

  const year2 = hoy.getFullYear()
  const month2 = hoy.getMonth()
  const day2 = hoy.getDate()

  // Compare year
  return (
    year1 < year2 ||
    // Compare month
    (month1 < month2 && year1 == year2) ||
    // Compare day
    (day1 < day2 && year1 == year2 && month1 == month2)
  )
}

export const dateToday = (): string => {
  const date = new Date()

  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0') // Months are zero-based
  const year = date.getFullYear()

  return `${year}-${month}-${day}`
}
export const dateFullToday = (): string => {
  const date = new Date()

  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0') // Months are zero-based
  const year = date.getFullYear()
  const hh = String(date.getHours()).padStart(2, '0')
  const min = String(date.getMinutes()).padStart(2, '0')

  return `${year}-${month}-${day} ${hh}-${min}`
}
export const formatDateToYYYY_MM_DD_HH_MM = (date: Date | undefined): string => {
  if (typeof date === 'undefined') {
    date = new Date()
  }

  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0') // Months are zero-based
  const year = date.getFullYear()
  const hh = String(date.getHours()).padStart(2, '0')
  const min = String(date.getMinutes()).padStart(2, '0')

  return `${year}-${month}-${day} ${hh}:${min}`
}
export const formatDateToYYYY_MM_DD = (date: Date | undefined): string => {
  if (typeof date === 'undefined') {
    date = new Date()
  }
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0') // Months are zero-based
  const year = date.getFullYear()

  return `${year}-${month}-${day}`
}
export const formatDateToMMYYYY = (date: Date | undefined): string => {
  if (typeof date === 'undefined') {
    return ''
  }
  const month = String(date.getMonth() + 1).padStart(2, '0') // Months are zero-based
  const year = date.getFullYear()

  return `${month}-${year}`
}
export const getDateFromDb = (date: string | undefined | Date) => {
  let aux = ''

  if (date instanceof Date || typeof date === 'undefined') {
    aux = formatDateToYYYY_MM_DD(date)
  } else {
    aux = date
  }

  return aux.substring(0, 10)
}
export const vehicleHrefReturn = (estado: number, id: string) => {
  return vehCliHrefReturn(estado, id, true)
}
export const clientHrefReturn = (estado: number, id: string) => {
  return vehCliHrefReturn(estado, id, false)
}
const vehCliHrefReturn = (estado: number, id: string, isVehicle: boolean) => {
  let href = ''
  let creatingDoc = true

  switch (estado) {
    //Aca se agregan otros casos
    case VehicleState.Cotizables:
      href = AppPath.Estimates
      break
    case VehicleState.Consignables:
      href = AppPath.Consignments
      break
    case VehicleState.Reservables:
      href = AppPath.Reservations
      break
    case VehicleState.NotasVendibles:
      href = AppPath.SaleNotes
      break
    case VehicleState.Creditables:
      href = AppPath.Loans
      break
    case VehicleState.Comprables:
      href = AppPath.Purchases
      break
    case VehicleState.Negociables:
      return href

    default:
      creatingDoc = false
      href = `/${isVehicle ? AppPath.Vehicles : AppPath.Clients}/view/${id}`
      break
  }

  if (creatingDoc) {
    href = `/selection/${href}?${isVehicle ? 'vehicleId' : 'clientId'}=${id}`
  }

  return href
}

/* Llamar antes de enviar a mongoDB , asumiendo 
que la fecha esta en format YYYY-MM-DDTHH:MM
*/
export const stringToMongoDateTime = (date: string | undefined) => {
  const dateString = `${date}:00.000Z`

  const myDate = new Date(dateString)
  const offset = myDate.getTimezoneOffset() * 60 * 1000

  return new Date(myDate.getTime() + offset).toISOString()
}

export const normalizeDate = (date: string | undefined) => {
  if (!date) return ''

  if (date.indexOf('T') >= 0) return date

  const dateString = `${date}T00:00:00.000Z`

  const myDate = new Date(dateString)
  const offset = myDate.getTimezoneOffset() * 60 * 1000

  return new Date(myDate.getTime() + offset).toISOString()
}

export const getToken = () => localStorage.getItem('authToken')

export const isError = (obj: unknown): obj is Error => obj instanceof Error

export const transformToExpenseFormat = (expenses: Expense[]): ExpenseFormat[] => {
  return expenses.map((expense) => ({
    ...expense, // Spread the existing Expense properties
    cost_format: `${formatWithThousandSep(expense.cost)}`,
    date: fechaIsoToYYYY_MM_DD(expense.date),
  }))
}

export const MessageCrea = (doc: AppPath, accion: number): string => {
  let nom = 'desconocido'
  let accionDesc = 'desconocida'

  switch (doc) {
    case AppPath.Reservations:
      nom = 'Reserva'

      if (accion == ReservaAction.Crear) {
        accionDesc = 'creada'
      } else if (accion == ReservaAction.Modificar) {
        accionDesc = 'modificada'
      } else if (accion == ReservaAction.Archivar) {
        accionDesc = 'archivada'
      } else if (accion == ReservaAction.Anular) {
        accionDesc = 'anulada'
      }
      break
    case AppPath.SaleNotes:
      nom = 'Nota de venta'

      if (accion == NvvAction.Crear) {
        accionDesc = 'creada'
      } else if (accion == NvvAction.Modificar) {
        accionDesc = 'modificada'
      } else if (accion == NvvAction.Anular) {
        accionDesc = 'anulada'
      } else if (accion == NvvAction.Aprobar) {
        accionDesc = 'aprobada'
      }
      break
  }

  return `${nom} ${accionDesc}.`
}
export const makeCreditDescription = (c: Credit) => {
  return c.institution
}

export interface CuadroPago {
  minimum_format: string
  fee_format: string
  charge_format: string
  fee_iva: string
  minimum_iva: string
}
export interface ClientUser {
  client: {
    firstname: string
    lastname: string
    nin: string
  }
  profile_name?: string
  city?: string
}

export const CompleteMark = (content: string, consig?: ClientUser | null, cuadro?: CuadroPago) => {
  let cont = content

  if (consig) {
    const client_name = `${consig.client.firstname} ${consig.client.lastname}`
    cont = content.replace(/\?NOMBRE-CLIENTE\?/g, client_name.trim()).replace(/\?RUT-CLIENTE\?/g, consig.client.nin)

    const profile_name = consig.profile_name ?? 'NOMBRE_VENDEDOR'
    const city = consig.city ?? 'CIUDAD'

    cont = cont.replace(/\$\{profile\.user\.name\}/g, profile_name).replace(/\?CIUDAD-CONSIGNACION\?/g, city)
  }

  if (cuadro) {
    cont = cont
      .replace(/\?COMISION-CONSIGNACION\?/g, cuadro.fee_format)
      .replace(/\?CARGO-CONSIGNACION\?/g, cuadro.charge_format)
      .replace(/\?MINIMO-COMISION-CONSIGNACION\?/g, cuadro.minimum_format)
      .replace(/\?MAS-COMISION-IVA-CONSIGNACION\?/g, cuadro.fee_iva)
      .replace(/\?MAS-MINIMO-COMISION-IVA-CONSIGNACION\?/g, cuadro.minimum_iva)
  }

  // const dobleNewLine = "\n\n"
  // cont = cont.replace(/\n/g, dobleNewLine)
  return cont
}
export const showPaymentWreserve = (cost: Payment | PaymentEx) => {
  const tag = 'fromReservation' in cost && cost.fromReservation ? '[Reserva] ' : ''

  return `${tag}${cost.document}`
}
// Function to insert st2 into st1 after the first token
// Para la direccion
export const insertAfterFirstToken = (
  st1: string | undefined,
  st2: string | number | undefined,
  st3: string | undefined
) => {
  if (!st1) return ''

  if (!st2) st2 = ''

  if (!st3) st3 = ''

  if ((st2 === '' || st2 === 0) && st3 === '') return st1

  let aux

  if (st3 === '') aux = st2
  else if (st2 === '') aux = st3
  else aux = `${st2} - ${st3}`
  // Find the index of the first comma
  const commaIndex = st1.indexOf(',')

  // If there's no comma, just return the original string concatenated with the second
  if (commaIndex === -1) {
    return st1 + ` ${aux}`
  }

  // Insert st2 after the first token and before the first comma
  return st1.slice(0, commaIndex) + ` ${aux}` + st1.slice(commaIndex)
}

export const shuffleWithSeed = (array: ArrayLike<unknown>, seed = 100) => {
  if (!Array.isArray(array)) {
    throw new TypeError('Input must be an array.')
  }

  if (typeof seed !== 'number' || !Number.isFinite(seed)) {
    throw new TypeError('Seed must be a finite number.')
  }

  // Seeded PRNG using Lehmer random number generator (LCG)
  const seededRandom = () => {
    const a = 1664525,
      c = 1013904223,
      m = 2 ** 32 // LCG constants

    seed = (a * seed + c) % m

    return seed / m
  }

  const shuffled = [...array] // Avoid mutating the original array

  let currentIndex = shuffled.length

  // Fisher-Yates Shuffle using seeded RNG
  while (currentIndex > 0) {
    const randomIndex = Math.floor(seededRandom() * currentIndex)
    currentIndex--
    ;[shuffled[currentIndex], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[currentIndex]]
  }

  return shuffled
}
